/**
 * SiteMapContextLayers — lane-2 viewport-streamed regional overlays.
 *
 * For each enabled refdata dataset, maintains ONE `google.maps.Data` layer
 * (built for thousands of features — far cheaper than per-feature Polygon
 * objects) and refreshes it from `refdataBbox` on debounced map idle.
 * Nothing here writes Firestore: context is display-only. The only bridge
 * back into saved site layers is the explicit per-feature "Save to site
 * layers" action in the click info window (`onPromoteFeature`).
 *
 * Refresh is an id-diff, not clear-and-redraw: features still in view keep
 * their Data.Feature identity, so panning never flickers the whole field.
 */
import { useEffect, useRef } from 'react';
import { useMap } from '@vis.gl/react-google-maps';

import {
  CONTEXT_DATASETS,
  contextDatasetById,
  contextFeatureName,
  type ContextDatasetDescriptor,
} from '../../lib/siteLayers/contextLayerCatalog';
import { refdataBbox, tryRefdata } from '../../services/refdata/refdataClient';
import type { RefdataFeature } from '../../types/refdata';

export interface ContextDatasetStatus {
  loading: boolean;
  /** Features currently drawn. */
  count: number;
  /** True when the per-viewport cap trimmed the result — zoom in for full coverage. */
  truncated: boolean;
  /** Dataset snapshot unavailable (unseeded / offline) — toggle stays but draws nothing. */
  unavailable: boolean;
}

export interface SiteMapContextLayersProps {
  enabledDatasetIds: ReadonlySet<string>;
  /** Per-dataset streaming status for the layers panel (counts, truncation). */
  onStatusChange?: (statuses: Readonly<Record<string, ContextDatasetStatus>>) => void;
  /**
   * Promote one streamed feature into the saved-layer lane. Receives the
   * dataset descriptor + raw refdata feature; the host owns conversion and
   * persistence. Omit ⇒ info windows render without the save action.
   */
  onPromoteFeature?: (descriptor: ContextDatasetDescriptor, feature: RefdataFeature) => void;
}

/** Per-viewport feature cap — matches refdataBbox server trimming. */
export const CONTEXT_VIEWPORT_FEATURE_LIMIT = 1500;
const IDLE_DEBOUNCE_MS = 350;

interface DatasetRuntime {
  data: google.maps.Data;
  /** Raw features by id — click promote needs the original geometry/props. */
  features: Map<string, RefdataFeature>;
  clickListener: google.maps.MapsEventListener;
}

function styleForDescriptor(
  d: ContextDatasetDescriptor,
): (feature: google.maps.Data.Feature) => google.maps.Data.StyleOptions {
  return (feature) => {
    const type = feature.getGeometry()?.getType();
    if (type === 'Point' || type === 'MultiPoint') {
      return {
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          scale: 3.5,
          fillColor: d.color,
          fillOpacity: 0.9,
          strokeColor: '#ffffff',
          strokeWeight: 1,
        },
      };
    }
    if (type === 'LineString' || type === 'MultiLineString') {
      return { strokeColor: d.color, strokeWeight: 1.5, strokeOpacity: 0.65 };
    }
    return {
      strokeColor: d.color,
      strokeWeight: 1,
      strokeOpacity: 0.55,
      fillColor: d.color,
      fillOpacity: 0.1,
    };
  };
}

export function SiteMapContextLayers({
  enabledDatasetIds,
  onStatusChange,
  onPromoteFeature,
}: SiteMapContextLayersProps) {
  const map = useMap();
  const runtimesRef = useRef<Map<string, DatasetRuntime>>(new Map());
  const statusesRef = useRef<Record<string, ContextDatasetStatus>>({});
  const infoWindowRef = useRef<google.maps.InfoWindow | null>(null);
  const generationRef = useRef(0);
  // Latest callbacks without re-running the map effect.
  const onStatusChangeRef = useRef(onStatusChange);
  onStatusChangeRef.current = onStatusChange;
  const onPromoteFeatureRef = useRef(onPromoteFeature);
  onPromoteFeatureRef.current = onPromoteFeature;

  const enabledKey = [...enabledDatasetIds].sort().join('|');

  useEffect(() => {
    if (!map || typeof google === 'undefined' || !google.maps?.Data) return;
    const runtimes = runtimesRef.current;

    const publishStatus = (datasetId: string, status: ContextDatasetStatus) => {
      statusesRef.current = { ...statusesRef.current, [datasetId]: status };
      onStatusChangeRef.current?.(statusesRef.current);
    };

    const openPromoteWindow = (
      descriptor: ContextDatasetDescriptor,
      feature: RefdataFeature,
      position: google.maps.LatLng,
    ) => {
      const content = document.createElement('div');
      content.style.cssText = 'font-family:monospace;font-size:11px;max-width:220px;';
      const title = document.createElement('div');
      title.style.cssText = 'font-weight:bold;margin-bottom:2px;';
      title.textContent = contextFeatureName(descriptor, feature.props);
      const source = document.createElement('div');
      source.style.cssText = 'opacity:0.7;margin-bottom:6px;';
      source.textContent = `${descriptor.label} — streamed context`;
      content.append(title, source);
      if (onPromoteFeatureRef.current) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.textContent = 'Save to site layers';
        btn.style.cssText = 'font:inherit;padding:2px 8px;cursor:pointer;';
        btn.addEventListener('click', () => {
          onPromoteFeatureRef.current?.(descriptor, feature);
          infoWindowRef.current?.close();
        });
        content.append(btn);
      }
      infoWindowRef.current ??= new google.maps.InfoWindow();
      infoWindowRef.current.setContent(content);
      infoWindowRef.current.setPosition(position);
      infoWindowRef.current.open({ map });
    };

    const ensureRuntime = (descriptor: ContextDatasetDescriptor): DatasetRuntime => {
      const existing = runtimes.get(descriptor.datasetId);
      if (existing) return existing;
      const data = new google.maps.Data({ map });
      data.setStyle(styleForDescriptor(descriptor));
      const features = new Map<string, RefdataFeature>();
      const clickListener = data.addListener('click', (e: google.maps.Data.MouseEvent) => {
        const id = e.feature?.getId();
        const raw = id != null ? features.get(String(id)) : undefined;
        if (raw && e.latLng) openPromoteWindow(descriptor, raw, e.latLng);
      });
      const runtime: DatasetRuntime = { data, features, clickListener };
      runtimes.set(descriptor.datasetId, runtime);
      return runtime;
    };

    const removeRuntime = (datasetId: string) => {
      const runtime = runtimes.get(datasetId);
      if (!runtime) return;
      runtime.clickListener.remove();
      runtime.data.setMap(null);
      runtimes.delete(datasetId);
      const { [datasetId]: _gone, ...rest } = statusesRef.current;
      statusesRef.current = rest;
      onStatusChangeRef.current?.(statusesRef.current);
    };

    const refreshDataset = async (descriptor: ContextDatasetDescriptor, generation: number) => {
      const bounds = map.getBounds();
      if (!bounds) return;
      const ne = bounds.getNorthEast();
      const sw = bounds.getSouthWest();
      publishStatus(descriptor.datasetId, {
        ...(statusesRef.current[descriptor.datasetId]
          ?? { count: 0, truncated: false, unavailable: false }),
        loading: true,
      });
      const result = await tryRefdata(descriptor.datasetId, () =>
        refdataBbox(descriptor.datasetId, {
          minLat: sw.lat(), minLng: sw.lng(), maxLat: ne.lat(), maxLng: ne.lng(),
        }, CONTEXT_VIEWPORT_FEATURE_LIMIT));
      // A newer refresh superseded this one, or the dataset was toggled off.
      if (generation !== generationRef.current || !runtimes.has(descriptor.datasetId)) return;
      const runtime = runtimes.get(descriptor.datasetId)!;
      if (result == null) {
        publishStatus(descriptor.datasetId, {
          loading: false, count: runtime.features.size, truncated: false, unavailable: true,
        });
        return;
      }
      const nextIds = new Set(result.features.map(f => f.id));
      const stale: google.maps.Data.Feature[] = [];
      runtime.data.forEach(f => {
        const id = f.getId();
        if (id == null || !nextIds.has(String(id))) stale.push(f);
      });
      for (const f of stale) {
        runtime.data.remove(f);
        const id = f.getId();
        if (id != null) runtime.features.delete(String(id));
      }
      for (const feature of result.features) {
        if (runtime.features.has(feature.id)) continue;
        runtime.features.set(feature.id, feature);
        runtime.data.addGeoJson({
          type: 'Feature',
          id: feature.id,
          geometry: feature.geom,
          properties: feature.props,
        });
      }
      publishStatus(descriptor.datasetId, {
        loading: false,
        count: runtime.features.size,
        truncated: result.truncated,
        unavailable: false,
      });
    };

    const refreshAll = () => {
      const generation = ++generationRef.current;
      for (const id of enabledDatasetIds) {
        const descriptor = contextDatasetById(id);
        if (!descriptor) continue;
        ensureRuntime(descriptor);
        void refreshDataset(descriptor, generation);
      }
    };

    // Reconcile toggles: drop disabled layers, then draw the enabled set.
    for (const datasetId of [...runtimes.keys()]) {
      if (!enabledDatasetIds.has(datasetId)) removeRuntime(datasetId);
    }
    refreshAll();

    let debounce: ReturnType<typeof setTimeout> | undefined;
    const idleListener = map.addListener('idle', () => {
      if (debounce) clearTimeout(debounce);
      debounce = setTimeout(refreshAll, IDLE_DEBOUNCE_MS);
    });

    return () => {
      if (debounce) clearTimeout(debounce);
      idleListener.remove();
    };
    // enabledKey is the memoized identity of enabledDatasetIds.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, enabledKey]);

  // Full teardown on unmount only.
  useEffect(() => () => {
    for (const runtime of runtimesRef.current.values()) {
      runtime.clickListener.remove();
      runtime.data.setMap(null);
    }
    runtimesRef.current.clear();
    infoWindowRef.current?.close();
  }, []);

  return null;
}
